import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { useSuppliers } from '../hooks/useSuppliers';
import { Input, Select } from '@/components/ui';
import type { Supplier, SupplierCreatePayload } from '../types/suppliers.types';

interface SupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier?: Supplier;
}

export function SupplierModal({ isOpen, onClose, supplier }: SupplierModalProps) {
  const { createSupplier, updateSupplier, isCreating, isUpdating } = useSuppliers();
  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SupplierCreatePayload>({
    defaultValues: {
      code: '',
      name: '',
      area: '',
      contact_person: '',
      phone: '',
      email: '',
      rating: null,
      debt_days: null,
      lead_time_days: null,
      status: 'active',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (supplier) {
        reset({
          code: supplier.code,
          name: supplier.name,
          area: supplier.area || '',
          contact_person: supplier.contact_person || '',
          phone: supplier.phone || '',
          email: supplier.email || '',
          rating: supplier.rating,
          debt_days: supplier.debt_days,
          lead_time_days: supplier.lead_time_days,
          status: supplier.status,
        });
      } else {
        reset({
          code: '',
          name: '',
          area: '',
          contact_person: '',
          phone: '',
          email: '',
          rating: null,
          debt_days: null,
          lead_time_days: null,
          status: 'active',
        });
      }
    }
  }, [isOpen, supplier, reset]);

  if (!isOpen) return null;

  const onSubmit = async (data: SupplierCreatePayload) => {
    const payload = {
      ...data,
      rating: data.rating ? Number(data.rating) : null,
      debt_days: data.debt_days ? Number(data.debt_days) : null,
      lead_time_days: data.lead_time_days ? Number(data.lead_time_days) : null,
    };

    if (supplier) {
      await updateSupplier({ id: supplier.id, payload });
    } else {
      await createSupplier(payload);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">
            {supplier ? 'Cập nhật nhà cung cấp' : 'Thêm mới nhà cung cấp'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <form id="supplier-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Mã NCC <span className="text-rose-500">*</span>
                </label>
                <Input
                  {...register('code', { required: 'Vui lòng nhập mã NCC' })}
                  placeholder="VD: NCC001"
                  className="w-full sm:text-sm text-xs uppercase font-mono"
                />
                {errors.code && <p className="mt-1 text-xs text-rose-500">{errors.code.message}</p>}
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Trạng thái
                </label>
                <Select {...register('status')} className="w-full sm:text-sm text-xs">
                  <option value="active">Đang hợp tác</option>
                  <option value="inactive">Ngừng giao dịch</option>
                </Select>
              </div>
            </div>

            <div>
              <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                Tên NCC <span className="text-rose-500">*</span>
              </label>
              <Input
                {...register('name', { required: 'Vui lòng nhập tên NCC' })}
                placeholder="Tên công ty hoặc cửa hàng..."
                className="w-full sm:text-sm text-xs"
              />
              {errors.name && <p className="mt-1 text-xs text-rose-500">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Khu vực / Địa chỉ
                </label>
                <Input
                  {...register('area')}
                  placeholder="Quận/Huyện, Tỉnh/TP..."
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Người liên hệ
                </label>
                <Input
                  {...register('contact_person')}
                  placeholder="Họ và tên..."
                  className="w-full sm:text-sm text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Số điện thoại
                </label>
                <Input
                  {...register('phone')}
                  placeholder="09..."
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  {...register('email')}
                  placeholder="example@domain.com"
                  className="w-full sm:text-sm text-xs"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 border-t border-slate-200 pt-4 mt-2">
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Đánh giá (1-5)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  step={0.1}
                  {...register('rating')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Công nợ (Ngày)
                </label>
                <Input
                  type="number"
                  min={0}
                  {...register('debt_days')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
              <div>
                <label className="block sm:text-sm text-xs font-medium text-slate-700 mb-1">
                  Thời gian GH (Ngày)
                </label>
                <Input
                  type="number"
                  min={0}
                  {...register('lead_time_days')}
                  className="w-full sm:text-sm text-xs"
                />
              </div>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 sm:text-sm text-xs font-medium text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            Hủy bỏ
          </button>
          <button
            type="submit"
            form="supplier-form"
            disabled={isSaving}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 sm:text-sm text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Đang lưu...' : 'Lưu lại'}
          </button>
        </div>
      </div>
    </div>
  );
}
